import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  Bug,
  Crosshair,
  Globe,
  Layers,
  Radar,
  Shield,
  Skull,
  Sparkles,
  Target,
  Workflow,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { api } from '../lib/api';

function severityBadgeClass(s) {
  const u = String(s || '').toLowerCase();
  if (u === 'critical') return 'bg-rose-500/15 text-rose-200 border-rose-500/30';
  if (u === 'high') return 'bg-amber-500/15 text-amber-200 border-amber-500/30';
  if (u === 'medium') return 'bg-sky-500/15 text-sky-200 border-sky-500/30';
  return 'bg-secondary text-secondary-foreground';
}

export function OperationsHubPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get('/api/operations-hub/dashboard', { timeoutMs: 30000 });
      setData(d);
    } catch (e) {
      toast.error(e.message || 'Failed to load operations hub');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runPlaybook = async (id) => {
    setBusyId(`pb:${id}`);
    try {
      const r = await api.post(`/api/operations-hub/playbooks/${encodeURIComponent(id)}/run`, {}, { timeoutMs: 20000 });
      if (r?.ok) toast.success('Playbook run queued', { description: r.message || r.run_id });
      await load();
    } catch (e) {
      toast.error(e.message || 'Run failed');
    } finally {
      setBusyId(null);
    }
  };

  const triage = async (id, action) => {
    setBusyId(`tq:${id}`);
    try {
      const r = await api.post(
        `/api/operations-hub/triage/${encodeURIComponent(id)}`,
        { action },
        { timeoutMs: 20000 }
      );
      if (r?.ok) toast.success(`Triage: ${action}`, { description: r.job_ref });
      await load();
    } catch (e) {
      toast.error(e.message || 'Triage failed');
    } finally {
      setBusyId(null);
    }
  };

  const meta = data?.meta;
  const fusion = data?.fusion;
  const intel = data?.intel;
  const irp = data?.irp;
  const paas = data?.paas;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-card via-card to-primary/[0.06] p-6 shadow-[0_0_40px_-12px_rgba(59,130,246,0.25)]">
        <div className="pointer-events-none absolute -right-24 -top-20 h-56 w-56 rounded-full bg-primary/15 blur-3xl" aria-hidden />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" />
              <Badge variant="outline" className="border-primary/40 text-xs font-normal text-primary">
                Security Operations Hub
              </Badge>
              {meta?.adapter_mode ? (
                <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                  {meta.adapter_mode}
                </Badge>
              ) : null}
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Fusion command</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              One surface for <strong className="text-foreground">agentic playbooks</strong>,{' '}
              <strong className="text-foreground">live intelligence</strong>,{' '}
              <strong className="text-foreground">unified IR</strong>, and{' '}
              <strong className="text-foreground">continuous assurance</strong> — wired to AERTA’s range, learning, and
              executor pipelines so you can go beyond a patchwork of disconnected tools.
            </p>
            {meta?.integration_note ? <p className="mt-2 text-xs text-muted-foreground/90">{meta.integration_note}</p> : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" asChild>
              <Link to="/fusion/cyber-range" className="gap-2">
                <Target className="h-4 w-4" />
                Cyber Range
              </Link>
            </Button>
            <Button type="button" variant="outline" size="sm" asChild>
              <Link to="/autonomous-pipeline" className="gap-2">
                <Workflow className="h-4 w-4" />
                Autonomous pipeline
              </Link>
            </Button>
            <Button type="button" variant="outline" size="sm" asChild>
              <Link to="/devguard" className="gap-2">
                <Shield className="h-4 w-4" />
                DevGuard
              </Link>
            </Button>
            <Button type="button" size="sm" onClick={load} disabled={loading}>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {loading && !data ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-10 text-muted-foreground">
            <Activity className="h-5 w-5 animate-spin text-primary" />
            Loading operations hub…
          </CardContent>
        </Card>
      ) : null}

      {data ? (
        <Tabs defaultValue="fusion" className="w-full">
          <TabsList className="grid h-auto w-full grid-cols-2 gap-1 p-1 lg:grid-cols-4">
            <TabsTrigger value="fusion" className="gap-2">
              <Zap className="h-4 w-4" />
              Fusion
            </TabsTrigger>
            <TabsTrigger value="intel" className="gap-2">
              <Radar className="h-4 w-4" />
              Intel &amp; surface
            </TabsTrigger>
            <TabsTrigger value="irp" className="gap-2">
              <Shield className="h-4 w-4" />
              IRP
            </TabsTrigger>
            <TabsTrigger value="paas" className="gap-2">
              <Layers className="h-4 w-4" />
              Assurance
            </TabsTrigger>
          </TabsList>

          <TabsContent value="fusion" className="space-y-4 animate-fade-in-up">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Workflow className="h-5 w-5 text-primary" />
                  AI playbook orchestration
                </CardTitle>
                <CardDescription>Hyperautomation runs with measurable steps — queue to SOAR when connectors are live.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-3">
                {(fusion?.playbooks || []).map((p) => (
                  <div
                    key={p.id}
                    className="flex flex-col rounded-xl border border-border/80 bg-background/40 p-4 transition-colors hover:border-primary/30"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-foreground">{p.name}</p>
                      <Badge variant={p.status === 'running' ? 'default' : 'secondary'}>{p.status}</Badge>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">Runs 24h: {p.runs_24h}</p>
                    <p className="text-[11px] text-muted-foreground">Last: {p.last_run_utc?.replace('T', ' ').slice(0, 19) || '—'} UTC</p>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {(p.automations || []).map((a) => (
                        <Badge key={a} variant="outline" className="text-[10px]">
                          {a}
                        </Badge>
                      ))}
                    </div>
                    <Button
                      className="mt-4"
                      size="sm"
                      variant="secondary"
                      disabled={busyId === `pb:${p.id}`}
                      onClick={() => runPlaybook(p.id)}
                    >
                      Run now
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Self-healing infrastructure</CardTitle>
                  <CardDescription>Policy drift correction with ETA — adapter to IaC / CSPM.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(fusion?.self_healing || []).map((s) => (
                    <div key={s.id} className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-sm">
                      <div className="flex justify-between gap-2">
                        <span className="font-medium text-foreground">{s.resource}</span>
                        <Badge variant="outline">{s.state}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{s.policy}</p>
                      {s.eta_minutes > 0 ? (
                        <p className="mt-1 text-[11px] text-primary">ETA ~{s.eta_minutes} min</p>
                      ) : null}
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Threat hunting</CardTitle>
                  <CardDescription>Scheduled hunts with hit telemetry — export to SIEM.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(fusion?.threat_hunts || []).map((h) => (
                    <div key={h.id} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2 text-sm">
                      <div>
                        <p className="font-medium text-foreground">{h.name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {h.query_lang} · {h.hits_24h} hits / 24h
                        </p>
                      </div>
                      <Crosshair className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Real-time triage &amp; response queue</CardTitle>
                <CardDescription>Suggested playbooks per signal — escalate to IRP cases when needed.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {(fusion?.triage_queue || []).map((q) => (
                  <div
                    key={q.id}
                    className="flex flex-col gap-3 rounded-xl border border-border/70 bg-card/50 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={severityBadgeClass(q.severity)}>{q.severity}</Badge>
                        <span className="text-xs text-muted-foreground">{q.source}</span>
                        <span className="text-xs text-muted-foreground">{q.age_minutes}m ago</span>
                      </div>
                      <p className="mt-1 font-medium text-foreground">{q.title}</p>
                      <p className="text-xs text-muted-foreground">Suggested: {q.suggested_playbook}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" disabled={!!busyId} onClick={() => triage(q.id, 'ack')}>
                        Ack
                      </Button>
                      <Button size="sm" variant="secondary" disabled={!!busyId} onClick={() => triage(q.id, 'escalate')}>
                        Escalate
                      </Button>
                      <Button size="sm" disabled={!!busyId} onClick={() => triage(q.id, 'run_playbook')}>
                        Run playbook
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="intel" className="space-y-4 animate-fade-in-up">
            <div className="grid gap-4 lg:grid-cols-3">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Globe className="h-5 w-5 text-primary" />
                    CTI — live feeds
                  </CardTitle>
                  <CardDescription>Curated IOCs with confidence — connect MISP / OpenCTI for production.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {(intel?.cti?.feeds || []).map((f) => (
                      <Badge key={f.name} variant="secondary" className="text-xs">
                        {f.name} · {f.ioc_count} IOC · {f.freshness_min}m fresh
                      </Badge>
                    ))}
                  </div>
                  {(intel?.cti?.highlights || []).map((h) => (
                    <div key={h.id} className="rounded-lg border border-border/60 bg-muted/20 p-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{h.type}</Badge>
                        <span className="font-mono text-xs text-foreground">{h.value}</span>
                        <span className="text-xs text-muted-foreground">conf {(h.confidence * 100).toFixed(0)}%</span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">{h.actor}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Radar className="h-4 w-4" />
                    Attack surface
                  </CardTitle>
                  <CardDescription>Bima-class discovery — score &amp; trending.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p className="text-3xl font-bold text-foreground">{intel?.attack_surface?.exposure_score ?? '—'}</p>
                  <p className="text-xs text-muted-foreground">Exposure score · trend {intel?.attack_surface?.trend}</p>
                  <p className="text-xs">New services 7d: {intel?.attack_surface?.new_services_7d}</p>
                  <p className="text-xs text-amber-200/90">Critical: {intel?.attack_surface?.critical_findings}</p>
                  {(intel?.attack_surface?.top_risks || []).map((r) => (
                    <div key={r.asset} className="rounded border border-border/50 px-2 py-1 text-xs">
                      <span className="font-medium">{r.asset}</span> — {r.issue}{' '}
                      <span className="text-muted-foreground">CVSS {r.cvss}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Skull className="h-5 w-5 text-primary" />
                  Orion-class malware sandbox
                </CardTitle>
                <CardDescription>Behavioral verdicts — queue depth {intel?.sandbox?.queue_depth ?? 0}.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                {(intel?.sandbox?.jobs || []).map((j) => (
                  <div key={j.id} className="rounded-xl border border-border/60 bg-background/40 p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-foreground">{j.filename}</span>
                      <Badge variant={j.verdict === 'clean' ? 'secondary' : 'destructive'}>{j.verdict}</Badge>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">{j.submitted_utc?.replace('T', ' ').slice(0, 19)} UTC</p>
                    {j.family_guess ? <p className="text-xs text-amber-200/90">Guess: {j.family_guess}</p> : null}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="irp" className="space-y-4 animate-fade-in-up">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="h-5 w-5 text-primary" />
                  Unified IR surface
                </CardTitle>
                <CardDescription>EDR · XDR · NVM health — one pane before you deep-link to vendor consoles.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-3">
                {Object.entries(irp?.unified_surface || {}).map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-border/60 bg-muted/15 p-4 uppercase">
                    <p className="text-[10px] text-muted-foreground">{k}</p>
                    <p className="mt-1 text-sm font-medium capitalize text-foreground">{v.vendor}</p>
                    <Badge className="mt-2" variant={v.health === 'ok' ? 'default' : 'destructive'}>
                      {v.health}
                    </Badge>
                    <p className="mt-2 text-[10px] text-muted-foreground">{v.last_sync_utc?.slice(0, 19)}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Cases</CardTitle>
                  <CardDescription>MTTR target {irp?.sla?.mttr_minutes_target}m · 30d {irp?.sla?.mttr_minutes_30d}m</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(irp?.cases || []).map((c) => (
                    <div key={c.id} className="rounded-lg border border-border/60 p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs text-primary">{c.id}</span>
                        <Badge variant="outline">{c.priority}</Badge>
                        <Badge variant="secondary">{c.status}</Badge>
                      </div>
                      <p className="mt-1 font-medium text-foreground">{c.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {c.owner} · {c.alerts_linked} alerts
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Also in AERTA</CardTitle>
                  <CardDescription>Native alerts &amp; events wire here when you enable DB-backed sensors.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    Open <Link className="text-primary underline" to="/events">Events</Link> for log stream,{' '}
                    <Link className="text-primary underline" to="/graph-findings">Graph Findings</Link> for correlated
                    entities.
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/events">
                      Go to events <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="paas" className="space-y-4 animate-fade-in-up">
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Bug className="h-5 w-5 text-primary" />
                    Pandava-class penetration workflows
                  </CardTitle>
                  <CardDescription>Continuous testing with framework mapping — links to executor &amp; Kali bridge.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(paas?.pentest?.pipelines || []).map((p) => (
                    <div key={p.id} className="rounded-xl border border-border/60 bg-background/40 p-4">
                      <p className="font-medium text-foreground">{p.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.cadence} · last findings {p.last_findings}
                      </p>
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-1">
                    {(paas?.pentest?.framework_alignment || []).map((f) => (
                      <Badge key={f} variant="outline" className="text-[10px]">
                        {f}
                      </Badge>
                    ))}
                  </div>
                  <Button variant="secondary" size="sm" asChild>
                    <Link to="/runs">Executor runs</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Korava-class bug bounty orchestration</CardTitle>
                  <CardDescription>Verified researchers, SLA-backed remediation, retest loops.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(paas?.bounty?.programs || []).map((b) => (
                    <div key={b.id} className="rounded-xl border border-border/60 p-4">
                      <p className="font-medium text-foreground">{b.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Researchers {b.researchers_verified} · open {b.open_submissions} · paid 30d ${b.paid_30d_usd}
                      </p>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">
                    Remediation SLA: crit {paas?.bounty?.remediation_sla_days?.critical}d · high{' '}
                    {paas?.bounty?.remediation_sla_days?.high}d
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">ISO 27001-aligned assurance</CardTitle>
                <CardDescription>Risk-weighted backlog &amp; retests — pair with Compliance workspace.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-3 text-sm">
                <div className="rounded-lg border border-border/60 p-3">
                  <p className="text-xs text-muted-foreground">Controls mapped</p>
                  <p className="text-2xl font-bold text-foreground">{paas?.compliance?.iso27001_controls_mapped}</p>
                </div>
                <div className="rounded-lg border border-border/60 p-3">
                  <p className="text-xs text-muted-foreground">Open retests</p>
                  <p className="text-2xl font-bold text-foreground">{paas?.compliance?.open_retests}</p>
                </div>
                <div className="rounded-lg border border-border/60 p-3">
                  <p className="text-xs text-muted-foreground">Risk-weighted backlog</p>
                  <p className="text-2xl font-bold text-foreground">{paas?.compliance?.risk_weighted_backlog}</p>
                </div>
                <div className="sm:col-span-3">
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/compliance">Open compliance</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : null}
    </div>
  );
}

export default OperationsHubPage;
